<template>
  <div class="i-quiz-report">
    <div class="header">
      <div class="header-label">Career Strategy Report</div>
      <div class="header-title">{{ content.title }}</div>
      <div class="header-meta">{{ metaLine }}</div>
      <div class="header-conf">This assessment is confidential. Not visible to employers, recruiters, or HR.</div>
    </div>

    <div class="card">
      <div class="card-label">{{ content.scoreName }}</div>
      <div class="score-row">
        <span class="score-num">{{ score }}</span>
        <span class="score-denom">/ 100</span>
      </div>
      <div class="score-badge">{{ scoreBadgeText }}</div>
      <div class="score-methodology">
        Based on your level, industry, tenure, performance history, negotiation leverage, and obstacle profile.
      </div>
      <div class="bar-wrap">
        <div
          class="bar-fill"
          :style="{ width: score + '%' }"
        ></div>
      </div>
      <div class="score-analysis">
        <div
          v-for="(para, i) in scoreParagraphs"
          :key="i"
          class="score-para"
          v-html="para"
        ></div>
      </div>
    </div>

    <div class="card card--dark">
      <div class="card-label">{{ content.compLabel }}</div>

      <template v-if="aboveMarket">
        <div class="comp-section-label">Your Comp (estimated)</div>
        <div class="comp-row">
          <div
            class="comp-bar comp-bar--amber"
            :style="{ width: Math.max(15, (currentComp / maxComp) * 100) + '%' }"
          >
            <span>${{ currentComp }}K</span>
          </div>
        </div>
        <div class="comp-section-label">Market Median for {{ levelLabel }} in {{ indLabel }}</div>
        <div class="comp-row">
          <div
            class="comp-bar comp-bar--navy"
            :style="{ width: Math.max(15, (marketRate / maxComp) * 100) + '%' }"
          >
            <span>${{ marketRate }}K</span>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="comp-section-label">Current Comp (estimated)</div>
        <div class="comp-row">
          <div
            class="comp-bar comp-bar--navy"
            :style="{ width: Math.max(15, (currentComp / maxComp) * 100) + '%' }"
          >
            <span>${{ currentComp }}K</span>
          </div>
        </div>
        <div class="comp-section-label">Market Rate for {{ levelLabel }} in {{ indLabel }}</div>
        <div class="comp-row">
          <div
            class="comp-bar comp-bar--amber"
            :style="{ width: Math.max(15, (marketRate / maxComp) * 100) + '%' }"
          >
            <span>${{ marketRate }}K</span>
          </div>
        </div>
      </template>
      <div class="comp-gap-badge">
        <div>
          <div
            class="comp-gap-num"
            :class="{ 'comp-gap-num--above': aboveMarket }"
          >
            <template v-if="aboveMarket"
              >+${{ Math.abs(compGap) }}K<br /><span class="comp-gap-sub">above market median</span></template
            >
            <template v-else
              >-${{ Math.abs(compGap) }}K<br /><span class="comp-gap-sub">annual gap today</span></template
            >
          </div>
          <div class="comp-gap-label">{{ compGapLabelText }}</div>
        </div>
      </div>
      <div class="cumulative-label">Cumulative Comp Gap Over Time</div>
      <div class="chart-legend">
        <div class="legend-item">
          <div class="legend-line legend-line--market"></div>
          <span>Market trajectory</span>
        </div>
        <div class="legend-item">
          <div class="legend-line legend-line--current"></div>
          <span>Your current trajectory</span>
        </div>
        <div class="legend-item">
          <div class="legend-line legend-line--gap"></div>
          <span>Cumulative gap</span>
        </div>
      </div>
      <div
        class="cumulative-summary"
        v-html="cumulativeSummary"
      ></div>
      <div
        class="compounding"
        v-html="compoundingText"
      ></div>
      <div class="cumulative-methodology">
        <div v-html="cumulativeMethodology"></div>
      </div>
    </div>

    <div class="cta-card">
      <div class="cta-headline">{{ ctaHeadline }}</div>
      <div class="cta-body">{{ ctaBody }}</div>
      <button
        class="btn"
        @click="goToCheckout"
      >
        {{ content.ctaText }}
      </button>
    </div>

    <div class="card">
      <div class="card-label">Top 3 Recommended Interventions</div>
      <div class="strategy-intro">{{ strategyIntro }}</div>
      <div class="strategies">
        <div
          v-for="(s, i) in content.strategies"
          :key="i"
          class="strategy"
        >
          <div class="strategy-header">
            <div class="strategy-num">{{ i + 1 }}</div>
            <div class="strategy-title">{{ s.title }}</div>
          </div>
          <div class="strategy-desc">{{ s.desc }}</div>
          <div
            v-if="s.outcome"
            class="strategy-outcome"
          >
            {{ s.outcome }}
          </div>
        </div>
      </div>
    </div>

    <div class="coach-card">
      <img
        :src="Linda"
        :alt="content.coachName + ' — Executive Career Coach'"
        class="coach-photo"
      />
      <div class="coach-inner">
        <div class="coach-eyebrow">Your Coach</div>
        <div class="coach-name">{{ content.coachName }}</div>
        <div class="coach-title">{{ content.coachTitle }}</div>
        <div class="coach-bio">{{ content.coachBio }}</div>
        <div class="coach-cta">
          <!-- Stalled: closing CTA replaces primary CTA -->
          <template v-if="isStalled">
            <div class="closing-headline">{{ stalledClosingCta.headline }}</div>
            <div
              class="closing-body"
              v-html="stalledClosingCta.body"
            ></div>
            <button
              class="btn"
              @click="goToCheckout"
            >
              {{ content.ctaText }}
            </button>
          </template>
          <!-- All other paths: standard primary CTA -->
          <template v-else>
            <button
              class="btn"
              @click="goToCheckout"
            >
              {{ content.ctaText }}
            </button>
          </template>
          <div class="coach-cta-sub">
            60-minute strategy session with Linda. Full career strategy, not a sales pitch.
          </div>
        </div>
      </div>
    </div>

    <div class="section">
      <div class="section-label">
        {{ isStalled ? "Coaching Outcomes" : "Real Results From Recent Clients" }}
      </div>

      <!-- Stalled: detailed case study cards -->
      <template v-if="isStalled">
        <div class="proof-grid">
          <div
            v-for="(cs, i) in stalledCaseStudies"
            :key="i"
            class="case-study"
          >
            <div class="case-header">{{ cs.header }}</div>
            <div class="case-title">{{ cs.title }}</div>
            <div class="case-body">{{ cs.body }}</div>
            <div class="case-note">Details anonymized at client request. Outcomes verified by coaching team.</div>
          </div>
        </div>
      </template>

      <!-- All other paths: testimonial quotes -->
      <template v-else>
        <div class="proof-grid">
          <div
            v-for="(t, i) in content.testimonials"
            :key="i"
            class="proof-case"
          >
            <div class="proof-quote">“{{ t.quote }}”</div>
            <div class="proof-attr">{{ t.attr }}</div>
          </div>
        </div>
      </template>
    </div>

    <div class="section">
      <div class="faq">
        <div
          v-for="(item, i) in faqItems"
          :key="i"
          class="faq-item"
          :class="{ open: openFaq === i }"
        >
          <div
            class="faq-q"
            @click="toggleFaq(i)"
          >
            {{ item.q }}
          </div>
          <div class="faq-a">{{ item.a }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue"
import {
  getMarketRate,
  getCompBand,
  computeScore,
  getPathContent,
  adaptContentForLevel,
  FAQ_ITEMS,
  FAQ_ITEMS_STALLED
} from "@/composables/useQuestionnaire.js"

// ASSETS
import Linda from "@/assets/images/linda.jpg"

const props = defineProps({ answers: Object })
const openFaq = ref(null)
const faqItems = computed(() => (a.value.situation === "stalled" ? FAQ_ITEMS_STALLED : FAQ_ITEMS))

const a = computed(() => props.answers || {})
const content = computed(() => adaptContentForLevel(getPathContent(a.value.situation), a.value.roleLevel))
const score = computed(() => computeScore(a.value))

const marketRate = computed(() => {
  const base = getMarketRate(a.value.roleLevel || "director", a.value.industry || "other-ind")
  return Math.round(base * (content.value.compMarketMultiplier || 1))
})
const currentComp = computed(() => getCompBand(a.value.comp || "prefer-not"))
const compGap = computed(() => Math.round(marketRate.value - currentComp.value))
const maxComp = computed(() => Math.max(marketRate.value, currentComp.value) * 1.15)

const aboveMarket = computed(() => compGap.value <= 0)

const levelNames = {
  ic: "Individual Contributor",
  "senior-mgr": "Senior Manager",
  director: "Director",
  vp: "VP/SVP",
  "c-suite": "C-Suite",
  board: "Board-level"
}
const industryNames = {
  tech: "Technology",
  finserv: "Financial Services",
  healthcare: "Healthcare",
  consulting: "Consulting",
  consumer: "Consumer/Retail",
  industrial: "Manufacturing",
  "other-ind": "your industry"
}
const levelNamesLower = {
  ic: "Individual Contributor",
  "senior-mgr": "Senior Manager",
  director: "Director",
  vp: "VP/SVP",
  "c-suite": "C-Suite",
  board: "Board-level"
}
const industryNamesLower = {
  tech: "technology",
  finserv: "financial services",
  healthcare: "healthcare",
  consulting: "consulting",
  consumer: "consumer/retail",
  industrial: "manufacturing",
  "other-ind": "your industry"
}

const levelLabel = computed(() => levelNames[a.value.roleLevel] || "your level")
const indLabel = computed(() => industryNames[a.value.industry] || "your industry")
const levelLabelLower = computed(() => levelNamesLower[a.value.roleLevel] || "your level")
const indLabelLower = computed(() => industryNamesLower[a.value.industry] || "your industry")

const metaLine = computed(() => {
  const now = new Date()
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
  const level = (a.value.roleLevel || "director").replace(/-/g, " ").toUpperCase()
  const industry = (a.value.industry || "other-ind").replace(/-/g, " ").toUpperCase()
  return `${level} | ${industry} | ${dateStr}`
})

const scoreBadgeText = computed(() => {
  const s = score.value
  if (s >= 70) return "Strong Position — Underutilized"
  if (s >= 55) return content.value.scoreLabel
  return "Significant Gap Detected"
})

const ctaHeadline = computed(() => {
  if (aboveMarket.value) return `Your $${Math.abs(compGap.value)}K premium needs a protection strategy.`
  return `Your $${Math.abs(compGap.value)}K comp gap has a specific fix.`
})
const ctaBody = computed(() => {
  if (aboveMarket.value) return "Book 60 minutes to map the moves that maintain — or exceed — your current level."
  return "Book 60 minutes with a coach who has closed this gap before."
})

// Verbatim compounding text from original
const compoundingText = computed(() => {
  const neg = a.value.negotiationLeverage
  const neverNeg = neg === "not-neg" || neg === "never-neg"
  if (aboveMarket.value) {
    return (
      "Being above market median means your employer knows it too. <strong>Above-market professionals are 2.4x more likely to be targeted in restructuring</strong> — not because of performance, but because the cost-to-replace math favors it. The strategic question isn’t whether you’re paid well. It’s whether your <strong>next move</strong> maintains or exceeds this level. " +
      (neverNeg
        ? "You’ve historically <strong>not negotiated compensation</strong> — which means your current premium was likely earned through tenure, not leverage. That’s harder to replicate at a new company."
        : "Without a strategy for your next transition, <strong>above-market professionals frequently accept lateral or below-market offers</strong> under time pressure.")
    )
  }
  return (
    "The gap compounds because market rates grow at <strong>~4%/yr</strong> (driven by demand + inflation), while internal merit raises average <strong>~2%/yr</strong>. That 2 percentage-point annual drift is small in year 1, but it compounds. " +
    (neverNeg
      ? "You’ve historically <strong>not negotiated compensation</strong>, which means your baseline is likely already below market before the drift begins."
      : neg === "none"
        ? "Without competing offers, your negotiation leverage has been limited — which means the starting gap is likely <strong>larger than the market median suggests</strong>."
        : "")
  )
})

const compGapLabelText = computed(() => {
  if (aboveMarket.value) return "You’re well-compensated — but that creates a different risk."
  return `vs. market rate for ${levelLabel.value} in ${indLabel.value}`
})

// Verbatim generateScoreAnalysis from original
const scoreParagraphs = computed(() => {
  const ans = a.value
  const lv = levelLabelLower.value
  const ind = indLabelLower.value
  const arrow = '<span style="color:#1D4ED8;font-weight:700;margin-right:6px;">→</span>'
  const paras = []

  // Paragraph 1: role level + industry
  if (ans.situation === "laid-off") {
    paras.push(
      arrow +
        "<strong>" +
        lv +
        " professionals in " +
        ind +
        " who are displaced have a 90-day window</strong> where their market value is at peak. After that, hiring managers start asking why you’re still looking. The data is unambiguous: candidates who begin strategic outreach within 30 days of separation land roles averaging <strong>18% higher compensation</strong> than those who wait 90+ days. Your window is open, but it’s not indefinite."
    )
  } else if (ans.situation === "stalled") {
    paras.push(
      arrow +
        "At the <strong>" +
        lv +
        " level in " +
        ind +
        "</strong>, promotion decisions are made 6–12 months before the announcement. If you’re not already in those conversations, performance alone will not get you there. The professionals who break through at your level don’t work harder — they <strong>change who sees their work and how it’s framed</strong>. That’s a strategy problem, not an effort problem."
    )
  } else if (ans.situation === "burnout") {
    paras.push(
      arrow +
        "At the <strong>" +
        lv +
        " level in " +
        ind +
        "</strong>, burnout isn’t about workload — it’s about <strong>misalignment between your energy and your role architecture</strong>. The professionals who recover without starting over don’t just leave. They redesign: scope, reporting line, team size, and operating rhythm. Most of these variables are negotiable if you know they’re on the table."
    )
  } else if (ans.situation === "pivot") {
    paras.push(
      arrow +
        "<strong>" +
        lv +
        " professionals in " +
        ind +
        "</strong> who pivot successfully don’t apply cold into new industries. They leverage <strong>transferable credibility through bridging roles</strong> — positions where 70% of the skill set is what you already have and 30% is what you’re building toward. The professionals who try to leap directly take an average 22% income hit. The ones who bridge maintain or increase."
    )
  } else {
    paras.push(
      arrow +
        "At the <strong>" +
        lv +
        " level in " +
        ind +
        "</strong>, the moves that matter — C-suite, board seats, PE operating roles — are never posted. They’re filled through <strong>direct relationships with 3–5 decision makers</strong> who already know your work. If you’re waiting to be recruited, you’re competing in a pool that doesn’t include the best opportunities."
    )
  }

  // Paragraph 2: time + urgency
  const timeLabels = {
    lt1m: "less than a month",
    "1-3m": "1–3 months",
    "3-6m": "3–6 months",
    "6-12m": "6–12 months",
    gt12m: "over a year"
  }
  const timeText = timeLabels[ans.timeInSituation] || "some time"
  const urgencyHigh = ans.timeInSituation === "6-12m" || ans.timeInSituation === "gt12m"

  if (urgencyHigh && ans.situation === "laid-off") {
    paras.push(
      arrow +
        "You’ve been in this situation for <strong>" +
        timeText +
        "</strong>. At this point, the market has already formed assumptions about your candidacy. Every month beyond 6 narrows your positioning options. This isn’t a judgment — it’s a <strong>negotiation physics problem</strong>. The fix is repositioning your narrative, not applying harder."
    )
  } else if (urgencyHigh && ans.situation === "stalled") {
    paras.push(
      arrow +
        "You’ve been stuck for <strong>" +
        timeText +
        "</strong>. In our experience, professionals who have been passed over for more than 6 months at your level are almost never promoted internally without a forcing function. <strong>The path forward is either a structured internal case with a hard deadline, or external validation that changes the internal conversation.</strong>"
    )
  } else if (urgencyHigh) {
    paras.push(
      arrow +
        "You’ve been in this situation for <strong>" +
        timeText +
        "</strong>. Extended deliberation at your level has a measurable cost — not just in comp, but in the <strong>compounding opportunity cost</strong> of staying in a role that isn’t building toward where you want to be in 3 years. The professionals who eventually act almost universally say they wish they’d started sooner."
    )
  } else if (ans.timeInSituation === "lt1m" || ans.timeInSituation === "1-3m") {
    paras.push(
      arrow +
        "You’re <strong>" +
        timeText +
        " into this</strong> — which is actually ideal timing. The professionals who get the best outcomes start strategy work immediately, not after months of solo effort. <strong>Early movers at your level secure 15–25% better outcomes</strong> than those who wait until urgency forces their hand."
    )
  } else {
    paras.push(
      arrow +
        "At <strong>" +
        timeText +
        "</strong> in, you’re in the window where strategic intervention has the highest ROI. The initial emotional response has passed, but you haven’t yet developed the <strong>defensive patterns</strong> — lowered expectations, narrowed targeting — that erode outcomes over time."
    )
  }

  // Paragraph 3: negotiation leverage + obstacle
  const negLabels = {
    "two-plus": "multiple competing offers",
    one: "one competing offer",
    none: "no competing offers",
    "not-neg": "didn't negotiate",
    "never-neg": "have never negotiated compensation"
  }
  const negText = negLabels[ans.negotiationLeverage] || "limited leverage"
  const negWeak =
    ans.negotiationLeverage === "none" ||
    ans.negotiationLeverage === "not-neg" ||
    ans.negotiationLeverage === "never-neg"

  const obstacleInsights = {
    clarity:
      "Your stated obstacle — <strong>lack of clarity on what’s next</strong> — is the most common and most solvable constraint at your level. It’s rarely about options. It’s about having a framework to evaluate them against what actually drives your energy and performance.",
    how: "You know where you want to go but <strong>lack the execution strategy</strong>. This is the highest-leverage coaching problem — you don’t need more information, you need a sequenced plan with accountability.",
    network:
      "Your stated constraint is <strong>network access</strong>. At your level, this is almost never about knowing more people. It’s about activating the ones you already know with a specific, compelling ask instead of a vague “keeping my options open.”",
    positioning:
      "Your resume and positioning <strong>don’t reflect your true market value</strong>. This is the single most expensive problem at your level — it suppresses every conversation before it starts.",
    paralysis:
      "You’re in <strong>analysis paralysis</strong>. At your level, more research doesn’t reduce uncertainty — it increases it. The fix is a structured decision framework with a 2-week deadline, not more data.",
    comp: "Your concern is that <strong>roles matching your compensation don’t seem to exist</strong>. In our experience, that’s almost always a targeting problem, not a market problem.",
    fear: "Your primary blocker is <strong>fear of making the wrong move</strong>. This is the most rational-sounding obstacle and the most paralytic. The antidote isn’t confidence — it’s a reversibility analysis of each option."
  }
  const obstacleText =
    obstacleInsights[ans.primaryObstacle] ||
    "Your obstacle profile suggests a strategy gap that’s addressable with structured intervention."

  if (negWeak) {
    const negSentence =
      ans.negotiationLeverage === "not-neg"
        ? "You <strong>didn’t negotiate</strong> your last offer"
        : ans.negotiationLeverage === "never-neg"
          ? "You’ve <strong>never negotiated compensation</strong>"
          : "You last negotiated with <strong>" + negText + "</strong>"
    paras.push(
      arrow +
        negSentence +
        ". Professionals at your level who negotiate without competing offers leave an average of <strong>$15K–$40K per year on the table</strong> — and that gap compounds over every subsequent role. " +
        obstacleText
    )
  } else {
    paras.push(
      arrow +
        "You’ve previously negotiated with <strong>" +
        negText +
        "</strong>, which means you understand leverage mechanics. The question now is whether your next move creates the same leverage position. " +
        obstacleText
    )
  }

  return paras
})

const strategyIntro = computed(() => {
  const goalLabels = {
    "new-role": "landing a higher-level role",
    "higher-comp": "increasing your compensation",
    switch: "making a successful career transition",
    promote: "getting promoted internally",
    "clarity-goal": "gaining clarity on your direction",
    meaningful: "finding work that feels meaningful"
  }
  const goalText = goalLabels[a.value.primaryGoal] || "your next career move"
  return `Based on your answers, you prioritized ${goalText}. Here are the three interventions most relevant to your specific situation.`
})

function toggleFaq(i) {
  openFaq.value = openFaq.value === i ? null : i
}

const goToCheckout = () => {
  const checkoutUrl =
    import.meta.env.VITE_ENV === "prod"
      ? `https://buy.stripe.com/9B65kC0Ad7wy1Ox2QT6EU1N?locked_prefilled_email=${a.value.email}`
      : `https://checkout.jobtest.org/b/test_3cI9ASfv7g349gZdvx6EU0B?locked_prefilled_email=${a.value.email}`

  // Open the billing portal in the same tab (needed for safari)
  return (window.location.href = checkoutUrl)
}

// Cumulative gap calculations — mirrors drawCumulativeChart in original
const cumulativeData = computed(() => {
  const mRate = marketRate.value
  const cComp = currentComp.value
  const marketGrowth = 0.04
  const currentGrowth = 0.02
  const histYears = 3
  const fwdYears = 5

  const marketPts = []
  const currentPts = []
  for (let i = -histYears; i <= fwdYears; i++) {
    marketPts.push(mRate * Math.pow(1 + marketGrowth, i))
    currentPts.push(cComp * Math.pow(1 + currentGrowth, i))
  }

  let gap3 = 0
  let gap5 = 0
  for (let i = 1; i <= 3; i++) gap3 += Math.round(marketPts[histYears + i] - currentPts[histYears + i])
  for (let i = 1; i <= 5; i++) gap5 += Math.round(marketPts[histYears + i] - currentPts[histYears + i])

  return { gap3, gap5 }
})

const cumulativeSummary = computed(() => {
  const { gap3, gap5 } = cumulativeData.value
  const annualGap = compGap.value
  return (
    "<strong>Cumulative comp left on the table: $" +
    gap3 +
    "K</strong> over 3 years. If nothing changes, this grows to <strong>$" +
    gap5 +
    "K</strong> over the next 5 years." +
    (annualGap > 0
      ? " At <strong>$" +
        annualGap +
        "K/yr</strong> below market, every year without intervention is a year of compounding loss."
      : "")
  )
})

const cumulativeMethodology = computed(() => {
  const lvl = levelLabel.value
  const ind = indLabel.value
  return (
    "<strong>Methodology:</strong> Market rate from Levels.fyi 2025\u201326 medians for " +
    lvl +
    " in " +
    ind +
    ". Growth assumptions: 4% annual market appreciation, 2% effective internal growth (merit-only). Cumulative gap is simple sum of annual deltas, not NPV-discounted. Real gap is likely larger when accounting for equity appreciation differentials."
  )
})

// Stalled-only closing CTA (dynamic gap-aware)
const stalledClosingCta = computed(() => {
  const gap = compGap.value
  const above = gap <= 0
  const exposureLabel = score.value >= 60 ? "medium-high" : "significant"
  if (above) {
    return {
      headline: "Above-market comp doesn\u2019t protect itself.",
      body:
        "Your report shows a <strong>$" +
        Math.abs(gap) +
        "K premium above market</strong> and a <strong>" +
        exposureLabel +
        " exposure</strong>. One session to build a full protection strategy."
    }
  }
  return {
    headline: "The gap doesn\u2019t close itself.",
    body:
      "Your report shows a <strong>$" +
      Math.abs(gap) +
      "K annual comp gap</strong> and a <strong>" +
      exposureLabel +
      " exposure</strong>. One session to walk through both and build your plan."
  }
})

const isStalled = computed(() => a.value.situation === "stalled")

const stalledCaseStudies = [
  {
    header: "Senior Director \u2192 VP | Tech | Coaching Stage: Internal Promotion",
    title: "Passed over 3 times. Built the visibility case. Promoted to SVP in 6 months.",
    body: "Used the impact documentation framework to translate 3 years of delivery into organizational narrative language. Forced a 60-Day Rule conversation with his GM. Received a promotion decision in 47 days. Comp went from $310K to $415K \u2014 a $105K increase at promotion."
  },
  {
    header: "VP \u2192 Senior VP | Pharma | Coaching Stage: Market Test + Internal Leverage",
    title: "Invisible at her company. Ran a confidential market test. Got promoted with a 25% raise.",
    body: "Ran a 6-week market test while employed, generating 4 external offers. Returned to her manager with market data. The internal conversation shifted from \u201Cwe see you as future SVP\u201D to \u201Cwhat would it take to keep you.\u201D Promoted within 30 days. $75K comp increase."
  },
  {
    header: "Director \u2192 SVP | Finance | Coaching Stage: Exit + Upgrade",
    title:
      "Convinced he\u2019d topped out. Took an external role at 2 levels up. Stayed 18 months and proved he wasn\u2019t stalled \u2014 the company was.",
    body: "Applied the 60-Day Rule internally \u2014 received a vague answer. Ran an external search using the upgrade anchoring strategy. Received 3 offers. Accepted a role two levels above his prior title. Comp increased $140K in year one including equity acceleration."
  }
]
</script>

<style lang="scss" scoped>
.i-quiz-report {
  width: 100%;

  @include respond-to($breakpoint-md) {
    width: 600px;
  }

  .header {
    background: $white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 18px;
    border: 1px solid rgb(17 24 39 / 10%);

    @include respond-to($breakpoint-md) {
      padding: 36px 36px 32px;
    }

    &-label {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: $amber-mid;
      margin-bottom: 14px;
    }

    &-title {
      font-family: $serif;
      font-size: clamp(17px, 3.5vw, 24px);
      color: $ink;
      margin-bottom: 10px;
      font-weight: 400;
      line-height: 1.3;
    }

    &-meta {
      font-size: 13px;
      color: $ink-80;
      margin-bottom: 8px;
    }

    &-conf {
      font-size: 13px;
      color: $ink-80;
      margin-top: 14px;
      letter-spacing: 0.02em;
      font-weight: 500;
    }
  }

  .card {
    background: $white;
    border: 1px solid rgb(17 24 39 / 10%);
    border-radius: 12px;
    padding: 22px 20px;
    margin-bottom: 18px;

    @include respond-to($breakpoint-md) {
      padding: 32px 36px;
    }

    &--dark {
      border-color: rgb(17 24 39 / 12%);
    }
  }

  .card-label {
    font-family: $serif;
    font-size: clamp(17px, 3vw, 20px);
    font-weight: 400;
    color: rgb(17 24 39 / 90%);
    margin-bottom: 18px;
    display: block;
    line-height: 1.25;
  }

  .score-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 4px;
  }

  .score-num {
    font-family: $serif;
    font-size: clamp(40px, 8vw, 52px);
    font-weight: 400;
    color: rgb(17 24 39 / 92%);
    letter-spacing: -2px;
    line-height: 1;
  }

  .score-denom {
    font-family: $serif;
    font-size: clamp(22px, 4vw, 28px);
    color: $ink-40;
    font-weight: 400;
    letter-spacing: -1px;
  }

  .score-badge {
    display: inline-block;
    font-size: 13px;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 6px;
    margin-bottom: 10px;
    background: $amber-pale;
    color: $amber;
  }

  .score-methodology {
    font-size: 13px;
    color: $ink-40;
    font-style: italic;
    margin-bottom: 14px;
  }

  .bar-wrap {
    width: 100%;
    height: 10px;
    background: rgb(17 24 39 / 8%);
    border-radius: 5px;
    overflow: hidden;
    margin-bottom: 12px;
  }

  .bar-fill {
    height: 100%;
    border-radius: 5px;
    background: $amber-mid;
    transition: width 1.2s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .score-analysis {
    margin-top: 20px;
  }

  .score-para {
    font-size: 14px;
    color: rgb(17 24 39 / 92%);
    line-height: 1.7;
    padding: 14px 0;
    border-bottom: 1px solid rgb(10 14 26 / 6%);

    @include respond-to($breakpoint-md) {
      font-size: 15px;
    }

    :deep(strong) {
      font-weight: 700;
    }
  }

  .comp-section-label {
    font-size: 13px;
    color: $ink-60;
    margin-bottom: 4px;
  }

  .comp-row {
    margin-bottom: 14px;
  }

  .comp-bar {
    height: 30px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    color: $ink;
    flex-shrink: 0;
    transition: width 1.2s cubic-bezier(0.22, 1, 0.36, 1);
    min-width: 80px;

    span {
      margin-left: auto;
      padding-right: 12px;
    }

    &--navy {
      background: rgb(17 24 39 / 20%);
    }

    &--amber {
      background: $amber;
      color: $white;
    }
  }

  .comp-gap-badge {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 20px;
    padding: 18px 22px;
    background: rgb(17 24 39 / 6%);
    border: 1px solid rgb(17 24 39 / 10%);
    border-radius: 10px;
  }

  .comp-gap-num {
    font-family: $serif;
    font-size: clamp(24px, 5vw, 32px);
    font-weight: 400;
    color: $ink;
    letter-spacing: -0.02em;
    line-height: 1.2;

    &--above {
      color: $amber-mid;
    }
  }

  .comp-gap-sub {
    font-size: 16px;
    color: $ink-60;
    font-family: $serif;
  }

  .comp-gap-label {
    font-size: 13px;
    color: $ink-80;
    margin-top: 4px;
  }

  .cumulative-label {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: $amber-mid;
    margin-top: 24px;
    margin-bottom: 12px;
  }

  .chart-legend {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;

    span {
      font-size: 11px;
      color: $ink-60;
    }
  }

  .legend-line {
    width: 12px;
    height: 3px;
    border-radius: 2px;

    &--market {
      background: rgb(17 24 39 / 45%);
    }

    &--current {
      background: rgb(17 24 39 / 25%);
    }

    &--gap {
      background: $amber;
    }
  }

  .compounding {
    font-size: 14px;
    color: $ink-60;
    line-height: 1.65;
    margin-top: 14px;
    border-top: 1px solid rgb(10 14 26 / 8%);
    padding-top: 14px;

    :deep(strong) {
      color: rgb(17 24 39 / 90%);
      font-weight: 600;
    }
  }

  .cumulative-summary {
    font-size: 14px;
    color: rgb(17 24 39 / 75%);
    line-height: 1.65;
    margin-top: 14px;

    :deep(strong) {
      color: $ink;
      font-weight: 600;
    }
  }

  .cumulative-methodology {
    border-top: 1px solid rgb(10 14 26 / 8%);
    margin-top: 16px;
    padding-top: 14px;
    font-size: 12px;
    color: $slate;
    line-height: 1.55;

    :deep(strong) {
      font-weight: 600;
      color: $slate;
    }
  }

  .cta-card {
    background: rgb(29 78 216 / 8%);
    border: 1px solid rgb(29 78 216 / 22%);
    border-radius: 12px;
    padding: 24px 20px;
    margin-bottom: 18px;
    text-align: center;

    @include respond-to($breakpoint-md) {
      padding: 36px 32px;
    }
  }

  .cta-headline {
    font-family: $serif;
    font-size: clamp(20px, 3.5vw, 28px);
    color: $ink;
    font-weight: 400;
    margin-bottom: 10px;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  .cta-body {
    font-size: 14px;
    color: $ink-60;
    line-height: 1.6;
    margin-bottom: 22px;
  }

  .cta-sub {
    font-size: 12px;
    color: $slate;
    margin-top: 8px;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: $amber-mid;
    color: $white;
    font-family: $sans;
    font-size: 15px;
    font-weight: 600;
    padding: 14px 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition:
      opacity 0.2s,
      transform 0.15s;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;

    @include respond-to($breakpoint-md) {
      font-size: 16px;
      padding: 16px 48px;
    }

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
    }
  }

  .strategy-intro {
    font-size: 15px;
    color: $ink-60;
    line-height: 1.65;
    margin-bottom: 22px;
  }

  .strategies {
    display: flex;
    flex-direction: column;
  }

  .strategy {
    padding: 18px 0;
    border-top: 1px solid rgb(10 14 26 / 8%);

    &:first-child {
      border-top: none;
      padding-top: 0;
    }
  }

  .strategy-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 10px;
  }

  .strategy-num {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    background: $amber-mid;
    color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .strategy-title {
    font-family: $serif;
    font-size: clamp(16px, 3vw, 18px);
    font-weight: 400;
    color: $ink;
    line-height: 1.25;
  }

  .strategy-desc {
    font-size: 14px;
    color: $ink-60;
    line-height: 1.65;
    margin-left: 42px;
  }

  .strategy-outcome {
    margin-left: 42px;
    margin-top: 10px;
    font-size: 13px;
    font-weight: 600;
    color: $amber-mid;
    background: rgb(29 78 216 / 8%);
    padding: 8px 14px;
    border-radius: 6px;
    display: inline-block;
    line-height: 1.5;
  }

  .coach-card {
    border-radius: 12px;
    overflow: hidden;
    background: rgb(17 24 39 / 5%);
    border: 1px solid rgb(17 24 39 / 12%);
    margin-bottom: 18px;
  }

  .coach-photo {
    width: 100%;
    max-height: 260px;
    object-fit: cover;
    object-position: center center;
    display: block;

    @include respond-to($breakpoint-md) {
      max-height: 320px;
    }
  }

  .coach-inner {
    padding: 24px 22px 28px;

    @include respond-to($breakpoint-md) {
      padding: 28px 28px 32px;
    }
  }

  .coach-eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: $amber-mid;
    margin-bottom: 6px;
  }

  .coach-name {
    font-family: $serif;
    font-size: 18px;
    font-weight: 400;
    margin-bottom: 4px;
    color: $ink;
  }

  .coach-title {
    font-size: 13px;
    color: $ink-80;
    margin-bottom: 12px;
  }

  .coach-bio {
    font-size: 13px;
    line-height: 1.6;
    color: $ink-80;
  }

  .coach-cta {
    padding-top: 24px;
    margin-top: 28px;
    border-top: 1px solid rgb(10 14 26 / 8%);

    .btn {
      max-width: unset;
    }
  }

  .coach-cta-sub {
    font-size: 12px;
    color: $slate;
    text-align: center;
  }

  .section {
    margin-bottom: 32px;
  }

  .section-label {
    font-family: $serif;
    font-size: clamp(17px, 3vw, 20px);
    font-weight: 400;
    color: rgb(17 24 39 / 90%);
    margin-bottom: 16px;
    line-height: 1.25;
  }

  .proof-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .proof-case {
    padding: 20px;
    border-radius: 10px;
    background: rgb(17 24 39 / 4%);
    border-left: 3px solid $amber-mid;
  }

  .proof-quote {
    font-size: 14px;
    color: $ink-80;
    line-height: 1.65;
    font-style: italic;
    margin-bottom: 12px;
  }

  .proof-attr {
    font-size: 13px;
    color: $slate;
    font-weight: 500;
  }

  .case-study {
    border-radius: 10px;
    padding: 22px 24px;
    border: 1px solid #0a0e1a14;
    border-left: 3px solid $amber;
  }

  .case-header {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: $slate;
    margin-bottom: 8px;
  }

  .case-title {
    font-family: $serif;
    font-size: 16px;
    color: $ink;
    font-weight: 400;
    margin-bottom: 10px;
    line-height: 1.3;
  }

  .case-body {
    font-size: 13px;
    color: #111827b3;
    line-height: 1.6;
    margin-bottom: 10px;
  }

  .case-note {
    font-size: 11px;
    color: $ink-40;
    font-style: italic;
  }

  .closing-headline {
    font-family: $serif;
    font-size: clamp(20px, 3.5vw, 26px);
    color: $ink;
    font-weight: 400;
    margin-bottom: 12px;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  .closing-body {
    font-size: 14px;
    color: #111827b3;
    line-height: 1.65;
    margin-bottom: 22px;

    :deep(strong) {
      color: $ink;
      font-weight: 600;
    }
  }

  .faq {
    border-top: 1px solid $rule;
  }

  .faq-item {
    border-bottom: 1px solid $rule;
  }

  .open {
    .faq-a {
      max-height: 500px;
      margin-bottom: 18px;
    }

    .faq-q::before {
      content: "\2212";
    }
  }

  .faq-q {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    font-size: 15px;
    font-weight: 600;
    color: $ink;
    padding: 22px 0;

    @include respond-to($breakpoint-md) {
      font-size: 16px;
    }

    &::before {
      content: "+";
      font-size: 18px;
      color: $navy;
      min-width: 20px;
      text-align: center;
      margin-top: -2px;
      flex-shrink: 0;
    }
  }

  .faq-a {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.5s ease;
    margin-left: 32px;
    font-size: 14px;
    color: $ink-60;
    line-height: 1.65;
  }
}
</style>
